import { fileTypeFromBlob } from 'file-type';
import logger from './logger';

/**
 * Detects file type from file content using magic number detection
 * Only call this for files without extensions
 * 
 * @param {File} file - The File object to analyze
 * @returns {Promise<string>} - The detected extension (e.g., 'dcm', 'pdf', 'png') or empty string if unknown
 */
export const detectFileType = async (file) => {
    try {
        // Read first 4100 bytes for magic number detection
        // This is sufficient for most file type signatures including DICOM
        const blob = file.slice(0, 4100);
        
        const type = await fileTypeFromBlob(blob);
        
        if (type && type.ext) {
            // Ensure lowercase for consistency with backend validation
            const extension = type.ext.toLowerCase();
            logger.info(`🔍 Detected file type for "${file.name}": ${extension} (${type.mime})`);
            return extension;
        }
        
        // Unknown file type - return empty string per user requirement
        logger.debug(`🔍 Could not detect file type for "${file.name}", returning empty string`);
        return '';
        
    } catch (error) {
        // If detection fails (error reading file, etc.), return empty string
        logger.warn(`⚠️ Error detecting file type for "${file.name}":`, error);
        return '';
    }
};

/**
 * Batch detect file types for multiple files
 * Useful for processing many extensionless files efficiently
 * 
 * @param {File[]} files - Array of File objects to analyze
 * @returns {Promise<Map<string, string>>} - Map of file.name to detected extension
 */
export const batchDetectFileTypes = async (files) => {
    const detectionMap = new Map();
    
    // Process files in parallel (browser will handle concurrency limits)
    const promises = files.map(async (file) => {
        const extension = await detectFileType(file);
        detectionMap.set(file.name, extension);
    });
    
    await Promise.all(promises);
    return detectionMap;
};
